import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Alert, Button, Spinner } from 'react-bootstrap';
import ConsultationEditor from '../ConsultationEditor';
import ConsultationPrescriptionPanel from '../ConsultationPrescriptionPanel';
import ConsultationActModules from '../ConsultationActModules';
import ConsultationEditorHeaderHistory from '../ConsultationEditorHeaderHistory';
import ConsultationEditorPageFooter from '../ConsultationEditorPageFooter';
import { useDoctorWorkspace } from '../doctorWorkspaceContext';
import { useAuth } from '../../../context/AuthContext';
import consultationService from '../../../services/consultationService';
import { physicianCanPrescribe, doctorConsultationEditPath } from '../doctorPaths';
import { HEAD_DOCTOR_COPY } from '../../head-doctor/headDoctorCopy';
import { HeadDoctorPageHeader } from '../../head-doctor/ui';
import Icon from '../../common/Icons';
import '../ConsultationEditorPage.css';

import {
  getConsultationLockMessage,
  getConsultationStatusLabelFr,
  getConsultationStatusTone,
  isConsultationReadOnly,
  CONSULTATION_STATUS
} from '../utils/consultationStatus';

export default function DoctorConsultationEditorPage() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { workSiteId, workSites, toast, bumpDashboardStats, portalPaths, clinicalWorkspace } = useDoctorWorkspace();

  const isNewMode = consultationId === undefined || consultationId === null;
  const canPrescribe = physicianCanPrescribe(user?.role);
  const isHeadDoctor = clinicalWorkspace === 'headDoctor';
  const hdEditor = HEAD_DOCTOR_COPY.CONSULTATION_EDITOR;
  const isDirectMode = new URLSearchParams(location.search).get('direct') === '1';
  const canCaptureDirectVitals =
    isDirectMode && (clinicalWorkspace === 'physician' || clinicalWorkspace === 'headDoctor');

  const [record, setRecord] = useState(null);
  const [fetching, setFetching] = useState(!isNewMode);
  const [loadError, setLoadError] = useState('');
  const [identityFooterTarget, setIdentityFooterTarget] = useState(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [footerState, setFooterState] = useState({
    formId: 'consultation-editor-form-new',
    loading: false,
    isPendingConsultation: true
  });
  const consultationEditorRef = useRef(null);

  const selectedWorkSiteName =
    workSites.find((s) => String(s.id) === String(workSiteId))?.name || '';

  useEffect(() => {
    if (isNewMode) {
      setRecord(null);
      setLoadError('');
      setFetching(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setFetching(true);
      setLoadError('');
      try {
        const res = await consultationService.getConsultationById(consultationId, {
          site_id: workSiteId
        });
        if (cancelled) return;
        if (res.success && res.data) {
          setRecord(res.data);
        } else {
          setLoadError(res.message || 'Consultation introuvable.');
          setRecord(null);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.message || 'Chargement impossible.');
          setRecord(null);
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationId, isNewMode, workSiteId]);

  const reloadRecord = useCallback(async () => {
    if (!consultationId || isNewMode) return;
    try {
      const res = await consultationService.getConsultationById(consultationId, {
        site_id: workSiteId
      });
      if (res.success && res.data) setRecord(res.data);
    } catch {
      /* ignore */
    }
  }, [consultationId, isNewMode, workSiteId]);

  const handleSaved = useCallback(
    (payload) => {
      bumpDashboardStats();
      const c = payload?.consultation;

      if (payload?.wasCreate) {
        toast('success', 'Consultation enregistrée.');
        if (canPrescribe && c?.id) {
          navigate(doctorConsultationEditPath(c.id, portalPaths), { replace: true });
        } else {
          navigate(portalPaths.CONSULTATIONS);
        }
        return;
      }

      toast('success', payload?.completed ? 'Consultation terminée — dossier clôturé.' : 'Consultation mise à jour.');
      if (c) setRecord(c);
      setHistoryRefreshKey((k) => k + 1);
    },
    [bumpDashboardStats, toast, navigate, canPrescribe, portalPaths]
  );

  const goList = useCallback(() => navigate(portalPaths.CONSULTATIONS), [navigate, portalPaths]);

  const handleFooterStateChange = useCallback((next) => {
    setFooterState((prev) => ({ ...prev, ...next }));
  }, []);

  const handleSaveDraft = useCallback(() => {
    consultationEditorRef.current?.saveConsultation?.();
  }, []);

  const handleCompleteConsultation = useCallback(() => {
    consultationEditorRef.current?.saveConsultation?.({ complete: true });
  }, []);

  if (!user) return null;

  if (!workSiteId) {
    return (
      <div className="consult-editor-page recv-dash-pro--main">
        <Alert variant="warning">
          Sélectionnez un <strong>site de travail</strong> dans la barre supérieure pour créer ou modifier une
          consultation.
        </Alert>
        <Button variant="outline-primary" size="sm" as={Link} to={portalPaths.DASHBOARD}>
          Retour au tableau de bord
        </Button>
      </div>
    );
  }

  if (!isNewMode && fetching) {
    return (
      <div className="consult-editor-page consult-editor-loading" role="status" aria-live="polite">
        <Spinner animation="border" className="consult-editor-loading__spinner" />
        <p className="small mb-0">Chargement de la consultation…</p>
      </div>
    );
  }

  if (!isNewMode && loadError) {
    return (
      <div className="consult-editor-page recv-dash-pro--main">
        <Alert variant="danger">{loadError}</Alert>
        <Button variant="primary" size="sm" className="me-2" onClick={goList}>
          Retour à la liste des consultations
        </Button>
      </div>
    );
  }

  const editorTitle = isNewMode ? hdEditor.TITLE_NEW : hdEditor.TITLE_EDIT;

  const editorDescription = isNewMode ? hdEditor.SUBTITLE_NEW : hdEditor.SUBTITLE_EDIT;

  const consultationReadOnly = !isNewMode && isConsultationReadOnly(record);
  const isPendingConsultation =
    isNewMode || record?.status === CONSULTATION_STATUS.PENDING;

  const statusTone = getConsultationStatusTone(record?.status);
  const lockMessage = getConsultationLockMessage(record);

  const physicianHeroMeta = isNewMode
    ? 'Dossier clinique · enregistrez pour activer ordonnance et actes complémentaires'
    : getConsultationStatusLabelFr(record?.status, hdEditor);

  const consultationRefLine = record?.id
    ? [`#${record.id}`, canCaptureDirectVitals ? 'Prise en charge directe' : null].filter(Boolean).join(' · ')
    : '';

  const renderEditPageHeader = () => (
    <header className="consult-editor-page-header hd-no-print mb-4">
      <div className="consult-editor-page-header__nav">
        {isHeadDoctor ? (
          <Button variant="outline-secondary" size="sm" className="rounded-3" onClick={goList}>
            <Icon name="chevronLeft" size={14} className="me-1" />
            {hdEditor.BACK_LIST}
          </Button>
        ) : (
          <button type="button" className="consult-editor-hero__back btn btn-link p-0 border-0" onClick={goList}>
            <Icon name="chevronLeft" size={16} />
            Consultations
          </button>
        )}
        <div className="consult-editor-page-header__meta">
          {isHeadDoctor ? <span className="hd-page-header__badge">{hdEditor.BADGE}</span> : null}
          <span
            className={`consult-editor-status-pill consult-editor-status-pill--${statusTone}`}
          >
            {getConsultationStatusLabelFr(record?.status, hdEditor)}
          </span>
          <span className="consult-editor-page-header__ref">{consultationRefLine}</span>
        </div>
      </div>
      {lockMessage ? (
        <p className={`consult-editor-lock-banner consult-editor-lock-banner--${statusTone}`}>
          {lockMessage}
        </p>
      ) : null}
      <div className="consult-editor-page-header__body">
        <ConsultationEditorHeaderHistory
          consultation={record}
          workSiteId={workSiteId}
          buildEditPath={(id) => doctorConsultationEditPath(id, portalPaths)}
          refreshKey={historyRefreshKey}
        />
      </div>
    </header>
  );

  return (
    <div
      className={`consult-editor-page consult-editor-page--fixed-footer recv-dash-pro--main ${
        isHeadDoctor ? 'hd-consult-editor' : ''
      }`}
    >
      {isNewMode ? (
        isHeadDoctor ? (
          <HeadDoctorPageHeader
            badge={hdEditor.BADGE}
            title={editorTitle}
            description={editorDescription}
            actions={
              <Button variant="outline-secondary" size="sm" className="rounded-3" onClick={goList}>
                <Icon name="chevronLeft" size={14} className="me-1" />
                {hdEditor.BACK_LIST}
              </Button>
            }
          />
        ) : (
          <header className="consult-editor-hero">
            <button type="button" className="consult-editor-hero__back btn btn-link p-0 border-0" onClick={goList}>
              <Icon name="chevronLeft" size={16} />
              Consultations
            </button>
            <div className="consult-editor-hero__patient">
              <h1 className="consult-editor-hero__title">Nouvelle consultation</h1>
              <p className="consult-editor-hero__meta">{physicianHeroMeta}</p>
            </div>
            <div className="consult-editor-hero__chips">
              <span className="consult-editor-chip consult-editor-chip--new">Brouillon</span>
              {canCaptureDirectVitals ? (
                <span className="consult-editor-chip">Prise en charge directe</span>
              ) : null}
            </div>
          </header>
        )
      ) : record ? (
        renderEditPageHeader()
      ) : null}

      {canPrescribe ? (
        <ConsultationActModules
          consultation={record}
          consultationReady={!isNewMode && Boolean(record?.id)}
          isHeadDoctor={isHeadDoctor}
          enableNursingActs={!consultationReadOnly}
          workSiteId={workSiteId}
          onConsultationUpdated={reloadRecord}
          headDoctorCopy={hdEditor}
          readOnly={consultationReadOnly}
          toast={toast}
          onModuleSuccess={() => bumpDashboardStats()}
        />
      ) : null}

      <div
        className={`consult-editor-layout ${canPrescribe ? 'consult-editor-layout--split' : ''}`}
      >
        <main className="consult-editor-main">
          <ConsultationEditor
            ref={consultationEditorRef}
            consultation={isNewMode ? null : record}
            viewerSiteId={workSiteId}
            clinicalWorkspace={clinicalWorkspace}
            showNursingVitals={canCaptureDirectVitals}
            identityFooterTarget={identityFooterTarget}
            identityInPageFooter={!isNewMode}
            readOnly={consultationReadOnly}
            useFixedPageFooter
            onFooterStateChange={handleFooterStateChange}
            onCancel={goList}
            onSuccess={handleSaved}
          />
        </main>

        {canPrescribe ? (
          <aside className="consult-editor-aside" aria-label="Ordonnance médicale">
            <section className="consult-side-panel consult-side-panel--rx">
              <div className="consult-side-panel__head">
                <span className="consult-side-panel__icon" aria-hidden>
                  💊
                </span>
                <div>
                  <h2 className="consult-side-panel__title">
                    {isHeadDoctor ? hdEditor.RX_PANEL_TITLE : 'Ordonnance médicale'}
                  </h2>
                  <p className="consult-side-panel__hint">Médicaments · posologie · pharmacie</p>
                </div>
              </div>
              <div className="consult-side-panel__body">
                {!isNewMode && record?.id ? (
                  <ConsultationPrescriptionPanel
                    consultation={record}
                    selectedSiteId={workSiteId}
                    selectedSiteName={selectedWorkSiteName}
                    readOnly={consultationReadOnly}
                    onPrescriptionSuccess={() => {
                      bumpDashboardStats();
                      toast('success', 'Ordonnance enregistrée.');
                    }}
                  />
                ) : (
                  <p className="consult-side-panel__placeholder">
                    {isHeadDoctor
                      ? hdEditor.RX_PANEL_NEW_HINT
                      : 'Enregistrez la consultation pour ouvrir l’ordonnancier et lier la prescription au dossier.'}
                  </p>
                )}
              </div>
            </section>
          </aside>
        ) : null}
      </div>

      <ConsultationEditorPageFooter
        identityTargetRef={setIdentityFooterTarget}
        formId={footerState.formId}
        readOnly={consultationReadOnly}
        loading={footerState.loading}
        isEdit={!isNewMode}
        isPendingConsultation={isPendingConsultation}
        formDisabled={consultationReadOnly || footerState.loading}
        onCancel={goList}
        onSaveDraft={handleSaveDraft}
        onComplete={handleCompleteConsultation}
      />
    </div>
  );
}
